// MIDAS V3 — Multi-chat column strip (MODEL-PICKER Feature 2, spec §6)
//
// When ≥1 thread is attached, the chat region becomes a horizontal strip:
//   [PrimaryChat][AttachedChatColumnV3]×N
// with `overflow-x: auto`, each column `flex: 1 0 480px; min-width: 480px`
// (reuse of MIN_CHAT_WIDTH), 1px hairline between stripes. 3×480 = 1440 →
// horizontal scrollbar on smaller viewports (8px, always visible — the
// universal v3 scrollbar style).
//
// Design behaviours implemented here (design_handoff_multichat_v33):
//   • Column enter: 160ms ease-out fade + 14px slide; strip auto-scrolls to
//     reveal the new column. Honors prefers-reduced-motion (midas-v33.css).
//   • Detach: column collapses 120ms, then the redux detach lands; remaining
//     columns re-share width. Detaching all → the parent renders the exact
//     single-chat view again (no headers, no hairlines).
//   • Rail click on an attached row → scroll to its column + 700ms inset
//     accent flash ring (MIDAS_V3_FOCUS_ATTACHED_COLUMN_EVENT).
//   • Artifact choreography: while the artifact pane is open, only the
//     SOURCE column (artifactSourceThreadId; null = primary) stays visible —
//     siblings collapse to width 0 but STAY MOUNTED (preserves state AND
//     scroll positions); closing the pane restores all columns and the
//     strip's scroll position.
//   • In multi-column view EVERY column gets the slim 34px header (keeps the
//     machine-pill headers aligned); the PRIMARY column has no ✕.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { detachThread } from "../../../midasAttachSlice";
import { MIN_CHAT_WIDTH } from "../MidasV3PaneDividerV3";
import AttachedChatColumnV3 from "./AttachedChatColumnV3";
import { MIDAS_V3_FOCUS_ATTACHED_COLUMN_EVENT } from "./AttachColumnContext";

export interface MultiChatRowV3Props {
  /** The UNCHANGED primary chat surface (MidasChatAreaV3's single-view
   *  content) — mounted as the first column. */
  primary: React.ReactNode;
}

const DETACH_ANIM_MS = 120;

const prefersReducedMotion = (): boolean => {
  try {
    return (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch {
    return false;
  }
};

const MultiChatRowV3: React.FC<MultiChatRowV3Props> = ({ primary }) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");

  const columns = useAppSelector((s: any) => s.midasAttach.columns) as Array<{
    threadId: string;
    title: string;
    machineId: string | null;
    messages: unknown[];
    loading: boolean;
    error: string | null;
  }>;
  const artifactSourceThreadId = useAppSelector(
    (s: any) => s.midasAttach.artifactSourceThreadId
  ) as string | null;
  const dockVisible = useAppSelector(
    (s) =>
      s.midasPage.artifactTabs.length > 0 && s.midasPage.artifactPanelOpen
  );

  // Primary column header title = the active thread's title (rail source of
  // truth), falling back to "New chat" for a draft.
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);
  const historyThreads = useAppSelector(
    (s: any) => s.midasHistoryPage?.threads ?? []
  ) as Array<{ id: string; title: string }>;
  const pageThreads = useAppSelector(
    (s) => s.midasPage.threads
  ) as Array<{ id: string; title: string }>;
  const primaryTitle =
    (activeThreadId &&
      (historyThreads.find((t) => t.id === activeThreadId)?.title ||
        pageThreads.find((t) => t.id === activeThreadId)?.title)) ||
    (translate("midas.v3.newChat", { defaultValue: "New chat" }) as string);

  const stripRef = useRef<HTMLDivElement | null>(null);

  // ── Detach with 120ms collapse animation ──
  const [exitingIds, setExitingIds] = useState<string[]>([]);
  const handleRequestDetach = useCallback(
    (threadId: string) => {
      if (prefersReducedMotion()) {
        dispatch(detachThread(threadId));
        return;
      }
      setExitingIds((ids) =>
        ids.includes(threadId) ? ids : [...ids, threadId]
      );
      window.setTimeout(() => {
        dispatch(detachThread(threadId));
        setExitingIds((ids) => ids.filter((id) => id !== threadId));
      }, DETACH_ANIM_MS);
    },
    [dispatch]
  );

  // ── Auto-scroll to reveal a newly attached column ──
  const prevCount = useRef(columns.length);
  useEffect(() => {
    const el = stripRef.current;
    if (el && columns.length > prevCount.current) {
      el.scrollTo({
        left: el.scrollWidth,
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    }
    prevCount.current = columns.length;
  }, [columns.length]);

  // ── Rail focus event → scroll to column + 700ms flash ring ──
  const [flash, setFlash] = useState<{ threadId: string; id: number } | null>(
    null
  );
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ threadId: string }>).detail;
      if (!detail?.threadId) return;
      setFlash({ threadId: detail.threadId, id: Date.now() });
      const el = stripRef.current;
      if (!el) return;
      const col = el.querySelector(
        `[data-col-thread="${detail.threadId}"]`
      ) as HTMLElement | null;
      if (col) {
        const left =
          col.getBoundingClientRect().left -
          el.getBoundingClientRect().left +
          el.scrollLeft;
        el.scrollTo({
          left: Math.max(0, left - 24),
          behavior: prefersReducedMotion() ? "auto" : "smooth",
        });
      }
    };
    window.addEventListener(MIDAS_V3_FOCUS_ATTACHED_COLUMN_EVENT, handler);
    return () =>
      window.removeEventListener(
        MIDAS_V3_FOCUS_ATTACHED_COLUMN_EVENT,
        handler
      );
  }, []);

  // ── Artifact choreography: save/restore the strip's scroll position
  //    across the pane open (siblings collapse) → close (restore). ──
  const savedScrollLeft = useRef(0);
  const wasDockVisible = useRef(dockVisible);
  useEffect(() => {
    const el = stripRef.current;
    if (dockVisible && !wasDockVisible.current) {
      savedScrollLeft.current = el?.scrollLeft ?? 0;
    } else if (!dockVisible && wasDockVisible.current) {
      // Restore on the next frame — the collapsed columns need a layout
      // pass to regain their widths before scrollLeft can apply.
      const target = savedScrollLeft.current;
      requestAnimationFrame(() => {
        stripRef.current?.scrollTo({ left: target, behavior: "auto" });
      });
    }
    wasDockVisible.current = dockVisible;
  }, [dockVisible]);

  // Which columns are visible while the pane is open: only the SOURCE
  // (null = primary). Hidden columns stay mounted (state + scroll preserved).
  const primaryHidden = dockVisible && artifactSourceThreadId !== null;
  const isColumnHidden = (threadId: string): boolean =>
    dockVisible && artifactSourceThreadId !== threadId;

  const colBaseStyle: React.CSSProperties = {
    flex: `1 0 ${MIN_CHAT_WIDTH}px`,
    minWidth: MIN_CHAT_WIDTH,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    position: "relative",
  };

  return (
    <div
      ref={stripRef}
      className="mc-strip"
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        alignItems: "stretch",
        overflowX: "auto",
        overflowY: "hidden",
        background: "var(--bg)",
      }}
    >
      {/* ── Primary column — the EXISTING chat surface, no ✕. ── */}
      <div
        data-col-thread={activeThreadId || "__primary__"}
        className={`mc-col-collapsible${primaryHidden ? " mc-col-hidden" : ""}`}
        style={colBaseStyle}
      >
        {/* Slim header keeps the machine-pill headers aligned across
            columns (design: every column gets it in multi view). */}
        <div
          style={{
            height: 34,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "0 8px 0 14px",
            background: "var(--bg-soft)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <span
            title={primaryTitle}
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 12,
              fontWeight: 500,
              color: "var(--ink-2)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "var(--font-sans)",
            }}
          >
            {primaryTitle}
          </span>
        </div>
        {primary}
      </div>

      {/* ── Attached columns ── */}
      {columns.map((col) => {
        const exiting = exitingIds.includes(col.threadId);
        const hidden = isColumnHidden(col.threadId);
        return (
          <div
            key={col.threadId}
            data-col-thread={col.threadId}
            className={[
              "mc-col-enter",
              "mc-col-collapsible",
              exiting || hidden ? "mc-col-hidden" : "",
              exiting ? "mc-col-exit" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{
              ...colBaseStyle,
              borderLeft: "1px solid var(--border)",
            }}
          >
            {flash && flash.threadId === col.threadId && (
              <span
                key={flash.id}
                aria-hidden="true"
                className="mc-flash-ring"
              />
            )}
            <AttachedChatColumnV3
              column={col as any}
              onRequestDetach={handleRequestDetach}
            />
          </div>
        );
      })}
    </div>
  );
};

export default MultiChatRowV3;
